import React, { useState } from 'react';
import {
	ChartLine,
	ChevronDown,
	Check,
	LifeBuoy,
	LogOut,
	Plus,
	Settings,
	User
} from 'lucide-react';

import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { useMobileFrameContainer } from './MobileFrameContext';
import { NavSheet } from './NavSheet';
import { AdminAccessLogo } from './AdminAccessLogo';
import {
	adminMenuLabel,
	companySubtitle,
	planDisplayName,
	planSubtitle
} from './planDisplayHelpers';
import { PlanLogo } from './PlanLogo';
import { AdminAccessSummary, AdminCompanySummary, PlanStatus, PlanSummary } from './planTypes';
import type { ControlDeNavegacion } from './navControl';
import { AccionesDeCuenta, conTextos, TextosDeNavegacion } from './navTextos';

/**
 * ACTUALIZACIÓN (7 sep 2026): sí existe un equivalente real —
 * `Navigation/MobileTopBarV2.tsx` + `Navigation/useAccountNavigationData.ts`
 * (lógica compartida con `DesktopSidebarV2.tsx`) — pero solo en la rama
 * `feature/resumen-multi-plan`, todavía sin mergear a master a fecha de hoy
 * (última verificación: tip de esa rama en jul 2026, master ya en sep 2026 —
 * sigue divergida, no es un "ya casi"). La primera versión de este fichero
 * se escribió antes de encontrar esa rama, dando por hecho "no existe
 * ningún equivalente" — quedó corregido aquí, con la rama real como
 * referencia, no como excusa para seguir tratándolo como diseño 100% libre.
 *
 * DIVERGENCIAS DELIBERADAS frente al real (`MobileTopBarV2.tsx`), mantenidas
 * a propósito, no por desconocimiento:
 * - **Selector de plan**: el real usa un `DropdownMenu` anclado al botón de
 *   la derecha; aquí, un `Sheet` (bottom sheet) — decisión de UX ya tomada
 *   en esta sesión (WebView, targets táctiles, mismo criterio que
 *   `MobileBottomNav.tsx`), no un despiste.
 * - **Reparto plan/rol**: el real funde absolutamente todo (plan, Personal/
 *   Admin, Configuración/Soporte/Cerrar sesión) en el ÚNICO sheet/dropdown
 *   del avatar — aquí se separan en dos entradas (avatar = solo cuenta/rol,
 *   nombre de plan a la derecha = solo plan), a petición expresa, igual que
 *   la arquitectura de dos piezas de `DesktopSidebarV2.tsx`
 *   (`SidebarPlanSelector`/`SidebarUserMenu`) en vez de la fusión móvil real.
 *
 * LO QUE SÍ SE VERIFICÓ Y COINCIDE con el real, sin cambios necesarios:
 * - Copy literal (`ES.json` de esa rama): "Seleccionar plan"
 *   (`general.avatar.chooseView`) y "Ver resumen de mis planes"
 *   (`general.avatar.backToResumen`) — los dos ya estaban así aquí antes de
 *   verificarlo (por captura de referencia), coinciden exacto.
 * - El sheet de cuenta (Personal/Admin + Configuración/Soporte/Cerrar
 *   sesión, sin cabecera de avatar+nombre) coincide en estructura con el
 *   real — el real tampoco lleva esa cabecera.
 * - `planDisplayHelpers.ts` (`planDisplayName`/`planSubtitle`/
 *   `adminMenuLabel`) es literalmente el mismo fichero, sin diferencias.
 *
 * RESUMEN DE PLANES: la primera fila del selector se llama «Resumen de planes» (el real decía «Ver
 * resumen de mis planes», `general.avatar.backToResumen`) y es la opción que se elige cuando no hay ningún
 * plan: estando en la pantalla de aterrizaje multicuenta, el botón dice «Resumen de planes» en vez de
 * «Seleccionar plan» y la fila lleva el check. Con un solo plan no hay nada que elegir ni resumen al que
 * ir, pero sí hoja: con ese plan marcado y el botón «Añadir plan». En Admin, el mismo botón es el selector de EMPRESAS, con «Admin» al lado de
 * cada nombre: con varias SL abre una hoja y con una sola se enseña el campo sin hoja.
 *
 * Recibe los mismos datos que el Sidebar de escritorio (planes y acceso de administración),
 * para que las dos no puedan responder distinto.
 */

interface MobileTopBarPropsBase {
	/** Sin valor por defecto a propósito: un nombre inventado dentro del bloque
	 *  acaba en producción en cuanto alguien lo instale y no lo pase. */
	userName: string;
	/** Planes del usuario; los de Pendiente KYC no son seleccionables. */
	plans?: PlanSummary[];
	/** Empresas que administra, si tiene acceso de administración. */
	adminAccess?: AdminAccessSummary | null;
	/** Configuración, Soporte y Cerrar sesión de la hoja de cuenta (ver `AccionesDeCuenta`). */
	cuenta?: AccionesDeCuenta;
	/** Enseñar el cambio Personal / Admin; quien monta el bloque lo apaga si no hay vista personal. */
	showPersonalAdminSwitch?: boolean;
	/** Los textos que se ven, si no son los de por defecto (ver `navTextos.ts`). */
	textos?: Partial<TextosDeNavegacion>;
}

/** Ver `navControl.ts`: el par se conduce entero o no se conduce. */
export type MobileTopBarProps = MobileTopBarPropsBase & ControlDeNavegacion;

export const MobileTopBar = ({
	userName,
	plans = [],
	adminAccess = null,
	cuenta,
	showPersonalAdminSwitch = true,
	textos: textosPropios,
	activePlanId,
	onSelectPlan,
	isSummaryActive,
	onSelectSummary,
	onAddPlan,
	activeCompanyId,
	onSelectCompany,
	isAdminActive,
	onSelectPersonal,
	onSelectAdmin
}: MobileTopBarProps) => {
	const textos = conTextos(textosPropios);
	const selectablePlans = plans.filter((plan) => plan.status !== PlanStatus.PENDING_KYC);
	const frameContainer = useMobileFrameContainer();
	const [isPlanSheetOpen, setIsPlanSheetOpen] = useState(false);
	const [isAccountSheetOpen, setIsAccountSheetOpen] = useState(false);
	// Estado de reserva, para cuando el bloque NO viene conducido. Abrir y cerrar
	// las hojas (arriba) sí se queda dentro: es estado de la barra, no del producto.
	const [planPropio, setPlanPropio] = useState<string | undefined>(undefined);
	// Al entrar se está en el resumen de planes (real: ViewType.RESUMEN), sin plan elegido.
	const [resumenPropio, setResumenPropio] = useState(true);
	const [empresaPropia, setEmpresaPropia] = useState<string | undefined>(undefined);
	const [isCompanySheetOpen, setIsCompanySheetOpen] = useState(false);
	const [adminPropio, setAdminPropio] = useState(false);

	const selectedPlanId = onSelectPlan ? activePlanId : planPropio;
	const elegirPlan = (plan: PlanSummary) => {
		if (onSelectPlan) return onSelectPlan(plan);
		setPlanPropio(plan.id);
		setResumenPropio(false);
	};
	// Conducido, sin `onSelectSummary` no hay a dónde ir y la fila no se enseña.
	const enResumen = onSelectPlan ? (isSummaryActive ?? false) : resumenPropio;
	const irAlResumen = onSelectPlan
		? onSelectSummary
		: () => {
				setPlanPropio(undefined);
				setResumenPropio(true);
			};
	// Añadir plan: sin control desde fuera no hay a dónde ir pero el botón se enseña; conducido, sin
	// `onAddPlan` no se enseña.
	const anadirPlan = onSelectPlan ? onAddPlan : () => {};
	const empresaElegida = onSelectCompany ? activeCompanyId : empresaPropia;
	const elegirEmpresa = (company: AdminCompanySummary) =>
		onSelectCompany ? onSelectCompany(company) : setEmpresaPropia(company.id);

	// El tipo garantiza que el par viene entero o no viene.
	const enAdmin = onSelectAdmin ? isAdminActive : adminPropio;
	const irAPersonal = onSelectPersonal ?? (() => setAdminPropio(false));
	const irAAdmin = onSelectAdmin ?? (() => setAdminPropio(true));

	const initials = userName
		.split(' ')
		.map((n) => n[0])
		.slice(0, 2)
		.join('');
	// Con un solo plan no hay elección: es ese, y no hay resumen al que ir.
	const planUnico = selectablePlans.length === 1;
	// Con un solo plan y sin «Añadir plan» no hay nada que hacer en la hoja: el nombre, sin abrirla.
	const planFijo = planUnico && !anadirPlan;
	const activePlan = planUnico
		? selectablePlans[0]
		: selectablePlans.find((plan) => plan.id === selectedPlanId);
	const muestraSwitch = showPersonalAdminSwitch && (adminAccess?.companies.length ?? 0) > 0;
	// Conducida la cuenta, cada fila sale solo si tiene a dónde llevar (igual que en escritorio).
	const muestraConfiguracion = !cuenta || !!cuenta.onOpenSettings;
	const muestraSoporte = !cuenta || !!cuenta.supportHref;
	const muestraCerrarSesion = !cuenta || !!cuenta.onLogout;
	// Una línea separa grupos: solo si queda algo a los dos lados (nunca dos seguidas ni una suelta).
	const hayEnMedio = muestraConfiguracion || muestraSoporte;
	const lineaTrasElRol = hayEnMedio || muestraCerrarSesion;
	const empresas = adminAccess?.companies ?? [];
	const variasEmpresas = empresas.length > 1;
	// En Admin siempre hay una empresa elegida: la indicada o, si no, la primera.
	const empresaActiva = empresas.find((company) => company.id === empresaElegida) ?? empresas[0];

	return (
		<>
			{/*
			 * Altura MÍNIMA, no fija: el relleno del notch (safe-area-top) se restaría de
			 * la caja con h-14 y el avatar de 44 px se saldría de la barra. El espaciador
			 * de abajo crece igual, para que el contenido no quede debajo.
			 */}
			<header className="fixed top-0 left-0 right-0 z-40 flex min-h-14 items-center justify-between gap-2 border-b border-gray-200 dark:border-border bg-white/95 dark:bg-sidebar/95 px-3 backdrop-blur-lg safe-area-top">
				{/* Avatar (izquierda) — rol/cuenta: Personal/Admin + Configuración/Soporte/Cerrar sesión */}
				<Button
					variant="ghost"
					onClick={() => setIsAccountSheetOpen(true)}
					aria-label={textos.cuentaYRol}
					className="h-11 w-11 flex-none p-0 hover:bg-transparent"
				>
					<Avatar className="h-9 w-9">
						<AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-xs font-medium">
							{initials}
						</AvatarFallback>
					</Avatar>
				</Button>

				{/* Nombre de plan (derecha) — abre el selector de plan; con un solo plan, solo si hay «Añadir
				    plan» (si no, no hay nada que hacer en la hoja y es el nombre, sin flecha, como la empresa
				    única en Admin). En Admin, el campo de la empresa. */}
				{/* Sin planes, nada: ni hay que elegir ni resumen al que ir (igual que en escritorio). Salvo
				    que quien lo monta pase «Añadir plan»: para quien no tiene ninguno, es la acción. */}
				{!enAdmin && planFijo && (
					<span className="flex min-w-0 flex-1 items-center justify-end gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-foreground">
						<span className="min-w-0 truncate">{planDisplayName(selectablePlans[0])}</span>
					</span>
				)}
				{!enAdmin && !planFijo && (selectablePlans.length > 0 || Boolean(onAddPlan)) && (
					<Button
						variant="ghost"
						onClick={() => setIsPlanSheetOpen(true)}
						// `min-w-0` y `truncate`: un nombre largo («Plan de empleo · Empresa…») se recorta
						// en vez de salirse de la barra.
						className="h-auto min-w-0 flex-1 justify-end gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-transparent dark:text-foreground"
					>
						<span className="min-w-0 truncate">
							{activePlan
								? planDisplayName(activePlan)
								: enResumen
									? textos.resumenDePlanes
									: textos.seleccionarPlan}
						</span>
						<ChevronDown className="h-5 w-5 flex-none text-gray-400 dark:text-muted-foreground" />
					</Button>
				)}
				{enAdmin && empresas.length === 1 && (
					<span className="flex min-w-0 flex-1 items-center justify-end gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-foreground">
						<span className="min-w-0 truncate">{empresas[0].name}</span>
						<Badge variant="outline" className="flex-none font-normal">
							{textos.admin}
						</Badge>
					</span>
				)}
				{enAdmin && variasEmpresas && (
					<Button
						variant="ghost"
						onClick={() => setIsCompanySheetOpen(true)}
						className="h-auto min-w-0 flex-1 justify-end gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-transparent dark:text-foreground"
					>
						<span className="min-w-0 truncate">
							{empresaActiva ? empresaActiva.name : textos.seleccionarEmpresa}
						</span>
						{empresaActiva && (
							<Badge variant="outline" className="flex-none font-normal">
								{textos.admin}
							</Badge>
						)}
						<ChevronDown className="h-5 w-5 flex-none text-gray-400 dark:text-muted-foreground" />
					</Button>
				)}
			</header>

			{/* Espaciador para que el contenido no quede debajo de la barra fija, con el mismo
			    relleno de notch que ella. */}
			<div className="h-[calc(3.5rem+env(safe-area-inset-top,0px))]" />

			{/* Sheet de PLAN — mismo listado/copy que SidebarPlanSelector (escritorio),
			    entregado como sheet en vez de desplegable. */}
			<NavSheet
				open={isPlanSheetOpen}
				onOpenChange={setIsPlanSheetOpen}
				title={textos.tusPlanes}
				container={frameContainer}
			>
				<div className="space-y-1">
					{!planUnico && irAlResumen && (
						<>
							{/* «Resumen de planes» (la pantalla de aterrizaje multicuenta), arriba del todo: una
								    acción, no un plan más. Sin PlanLogo: el icono de gráfica de líneas, sin marco, en una
								    caja del ancho del logo, y el check cuando es donde se está. */}
							<Button
								variant="ghost"
								onClick={() => {
									irAlResumen();
									setIsPlanSheetOpen(false);
								}}
								className="flex min-h-12 w-full items-center gap-2.5 justify-start h-auto py-2 text-gray-700 hover:bg-gray-50 dark:text-foreground dark:hover:bg-card"
							>
								<span className="flex h-9 w-9 flex-none items-center justify-center">
									<ChartLine
										aria-hidden="true"
										className="h-5 w-5 text-gray-500 dark:text-muted-foreground"
									/>
								</span>
								<span className="flex-1 text-left text-sm font-medium">
									{textos.resumenDePlanes}
								</span>
								{enResumen && (
									<Check className="h-5 w-5 flex-none text-arca-green dark:text-primary" />
								)}
							</Button>
							<div className="my-1 border-t border-gray-100 dark:border-border" />
						</>
					)}
					{selectablePlans.map((plan) => (
						<Button
							key={plan.id}
							variant="ghost"
							onClick={() => {
								elegirPlan(plan);
								setIsPlanSheetOpen(false);
							}}
							className="flex min-h-12 w-full items-center gap-2.5 justify-start h-auto py-2 hover:bg-gray-50 dark:hover:bg-card"
						>
							<PlanLogo plan={plan} className="h-9 w-9 flex-none" />
							<span className="min-w-0 flex-1 text-left">
								{/* Con la empresa en el de empleo: dos planes de empleo se llaman igual. */}
								<span className="block truncate text-sm font-medium text-gray-900 dark:text-foreground">
									{planDisplayName(plan)}
								</span>
								{/* gray-500, no gray-400 (mismo bug de contraste real ya corregido
									    aquí que en SidebarPlanSelector/MobileBottomNav). */}
								{plan.status === PlanStatus.IN_PROGRESS ? (
									<Badge variant="warning" className="mt-0.5 font-normal">
										{textos.enProceso}
									</Badge>
								) : (
									<span className="block truncate text-sm text-gray-500 dark:text-muted-foreground">
										{planSubtitle(plan)}
									</span>
								)}
							</span>
							{activePlan?.id === plan.id && (
								<Check className="h-5 w-5 flex-none text-arca-green dark:text-primary" />
							)}
						</Button>
					))}

					{anadirPlan && (
						<>
							{/* Un botón secundario (outline), no una fila más: es una acción, y la de menos peso. */}
							<Button
								variant="outline"
								onClick={() => {
									anadirPlan();
									setIsPlanSheetOpen(false);
								}}
								className="mt-1 w-full justify-center gap-2 border-gray-200 text-gray-700 dark:border-border dark:text-foreground"
							>
								<Plus aria-hidden="true" className="h-4 w-4" />
								{textos.anadirPlan}
							</Button>
						</>
					)}
				</div>
			</NavSheet>

			{/* Sheet de EMPRESA — el selector de empresas de Admin (varias SL), mismo patrón que el de plan. */}
			<NavSheet
				open={isCompanySheetOpen}
				onOpenChange={setIsCompanySheetOpen}
				title={textos.tusEmpresas}
				container={frameContainer}
			>
				<div className="space-y-1">
					{empresas.map((company) => (
						<Button
							key={company.id}
							variant="ghost"
							onClick={() => {
								elegirEmpresa(company);
								setIsCompanySheetOpen(false);
							}}
							className="flex min-h-12 w-full items-center gap-2.5 justify-start h-auto py-2 hover:bg-gray-50 dark:hover:bg-card"
						>
							<AdminAccessLogo className="h-9 w-9" />
							<span className="min-w-0 flex-1 text-left">
								<span className="flex items-center gap-1.5">
									<span className="truncate text-sm font-medium text-gray-900 dark:text-foreground">
										{company.name}
									</span>
									<Badge variant="outline" className="flex-none font-normal">
										{textos.admin}
									</Badge>
								</span>
								<span className="block truncate text-sm text-gray-500 dark:text-muted-foreground">
									{companySubtitle(company)}
								</span>
							</span>
							{empresaActiva?.id === company.id && (
								<Check className="h-5 w-5 flex-none text-arca-green dark:text-primary" />
							)}
						</Button>
					))}
				</div>
			</NavSheet>

			{/* Sheet de CUENTA/ROL — mismo contenido que SidebarUserMenu (escritorio):
			    switch Personal/Admin + Configuración/Soporte/Cerrar sesión. */}
			<NavSheet
				open={isAccountSheetOpen}
				onOpenChange={setIsAccountSheetOpen}
				title={textos.tuCuenta}
				container={frameContainer}
			>
				{/* Sin cabecera de avatar+nombre (a petición expresa, 4 sep 2026) — el
					    avatar que abre este sheet ya identifica de quién es la cuenta, no
					    hace falta repetirlo dentro. Personal/Admin dejan de ser dos botones
					    lado a lado y pasan a ser dos filas más de la misma lista que
					    Configuración/Soporte/Cerrar sesión — un único listado continuo, con
					    el check a la derecha marcando cuál está activo. Admin usa
					    AdminAccessLogo (mismo bloque visual que PlanLogo en el sheet de
					    plan) en vez de un icono lucide genérico. */}
				<div className="space-y-1">
					{/* Mismo criterio que SidebarUserMenu en escritorio: sin empresas
						    administradas no hay entre qué elegir, y la fila de Admin saldría
						    con su logo y la etiqueta vacía. Las dos barras tienen que
						    responder igual. */}
					{muestraSwitch && (
						<>
							<Button
								variant="ghost"
								onClick={() => {
									irAPersonal();
									// Lleva a otra pantalla: la hoja no se queda tapándola.
									setIsAccountSheetOpen(false);
								}}
								className="flex min-h-12 w-full items-center gap-2.5 justify-start h-auto py-2 hover:bg-gray-50 dark:hover:bg-card"
							>
								<span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-gray-100 bg-sidebar-accent dark:border-border">
									<User className="h-4 w-4 text-primary" />
								</span>
								<span className="flex-1 text-left text-sm font-medium text-gray-900 dark:text-foreground">
									{textos.personal}
								</span>
								{!enAdmin && (
									<Check className="h-5 w-5 flex-none text-arca-green dark:text-primary" />
								)}
							</Button>
							<Button
								variant="ghost"
								onClick={() => {
									irAAdmin();
									setIsAccountSheetOpen(false);
								}}
								className="flex min-h-12 w-full items-center gap-2.5 justify-start h-auto py-2 hover:bg-gray-50 dark:hover:bg-card"
							>
								<AdminAccessLogo className="h-9 w-9" />
								<span className="flex-1 text-left text-sm font-medium text-gray-900 dark:text-foreground">
									{adminAccess && adminMenuLabel(adminAccess, textos)}
								</span>
								{enAdmin && (
									<Check className="h-5 w-5 flex-none text-arca-green dark:text-primary" />
								)}
							</Button>

							{lineaTrasElRol && (
								<div className="my-1 border-t border-gray-100 dark:border-border" />
							)}
						</>
					)}

					{muestraConfiguracion && (
						<Button
							variant="ghost"
							onClick={() => {
								cuenta?.onOpenSettings?.();
								setIsAccountSheetOpen(false);
							}}
							className="flex min-h-12 w-full items-center gap-2.5 justify-start h-auto py-2 text-gray-700 dark:text-foreground"
						>
							<span className="flex h-9 w-9 flex-none items-center justify-center">
								<Settings className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
							</span>
							{textos.configuracion}
						</Button>
					)}
					{muestraSoporte &&
						(cuenta?.supportHref ? (
							// Una página aparte: un enlace de verdad, que se abre en otra pestaña.
							<Button
								asChild
								variant="ghost"
								className="flex min-h-12 w-full items-center gap-2.5 justify-start h-auto py-2 text-gray-700 dark:text-foreground"
							>
								<a
									href={cuenta.supportHref}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => setIsAccountSheetOpen(false)}
								>
									<span className="flex h-9 w-9 flex-none items-center justify-center">
										<LifeBuoy className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
									</span>
									{textos.soporte}
								</a>
							</Button>
						) : (
							<Button
								variant="ghost"
								className="flex min-h-12 w-full items-center gap-2.5 justify-start h-auto py-2 text-gray-700 dark:text-foreground"
							>
								<span className="flex h-9 w-9 flex-none items-center justify-center">
									<LifeBuoy className="h-4 w-4 text-gray-500 dark:text-muted-foreground" />
								</span>
								{textos.soporte}
							</Button>
						))}

					{muestraCerrarSesion && (
						<>
							{hayEnMedio && <div className="my-1 border-t border-gray-100 dark:border-border" />}

							<Button
								variant="ghost"
								onClick={() => {
									cuenta?.onLogout?.();
									setIsAccountSheetOpen(false);
								}}
								className="flex min-h-12 w-full items-center gap-2.5 justify-start h-auto py-2 text-gray-600 dark:text-muted-foreground"
							>
								<span className="flex h-9 w-9 flex-none items-center justify-center">
									<LogOut className="h-4 w-4 " />
								</span>
								{textos.cerrarSesion}
							</Button>
						</>
					)}
				</div>
			</NavSheet>
		</>
	);
};
